// src/pages/dashboard/Attendance.js - ENHANCED VERSION WITH SMART STATS
import React, { useState, useEffect, useMemo } from 'react';
import {
  Upload,
  Button,
  Table,
  message,
  DatePicker,
  Space,
  Card,
  Row,
  Col,
  Input,
  Select,
  Modal,
  Form,
  TimePicker,
  Tabs,
  Alert,
  Tooltip,
  List
} from 'antd';
import {
  UploadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  UserOutlined,
  SearchOutlined,
  EditOutlined,
  EyeOutlined,
  ImportOutlined,
  CalendarOutlined,
  WarningOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  RiseOutlined,
  TeamOutlined,
  PlusOutlined
} from '@ant-design/icons';
import {
  formatTime,
  getAttendanceStatus,
  formatHoursMinutes,
  getLeaveTypeOptions,
  parseLeaveType
} from '../../services/attendanceUtils';
import timesheetService, { generateCompleteAttendanceMatrix } from '../../services/timesheetService';
import { supabase } from '../../services/supabase';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import './Attendance.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Page, PageHeader, StatGrid, StatCard, StatusPill } from '../../components/ui';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const { RangePicker } = DatePicker;
const { Option } = Select;

// getAttendanceStatus() returns antd colour names; map them onto theme tones
const STATUS_COLOR_TONES = {
  green: 'positive',
  orange: 'warning',
  red: 'danger',
  blue: 'primary',
  purple: 'primary',
  default: 'neutral',
};

const Attendance = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'import' ? 'import' : 'records');

  const [loading, setLoading] = useState(false);
  const [attendance, setAttendance] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [companyId, setCompanyId] = useState(null);
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [updatingRecord, setUpdatingRecord] = useState(false);

  const [importPreviewVisible, setImportPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [newEmployeesWarning, setNewEmployeesWarning] = useState(false);
  const [newEmployeesList, setNewEmployeesList] = useState([]);
  const [isEmptyRecord, setIsEmptyRecord] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    fetchCompanyId();
  }, []);

  useEffect(() => {
    if (companyId && dateRange?.length === 2) {
      fetchAttendance(companyId);
    }
  }, [companyId, dateRange]);

  useEffect(() => {
    applyFilters();
  }, [attendance, nameFilter, statusFilter]);

  // SMART STATISTICS - Calculate dynamically from filtered data
  const smartStats = useMemo(() => {
    const data = filteredAttendance.length > 0 ? filteredAttendance : attendance;
    
    if (data.length === 0) return null;

    // Calculate total hours
    let totalMinutes = 0;
    let workingDays = 0;
    let emptyDays = 0;
    let lateDays = 0;
    let earlyOutDays = 0;
    let regularDays = 0;
    let onTimeDays = 0;
    
    // Leave type breakdown
    const leaveBreakdown = {
      'SICK LEAVE': 0,
      'LOCAL LEAVE': 0,
      'PH': 0,
      'ABSENT': 0,
      'OFF': 0
    };
    
    // Track daily hours for best/worst
    const dailyHours = [];
    
    // Unique employees
    const uniqueEmployees = new Set();
    
    data.forEach(record => {
      uniqueEmployees.add(record.employee_id);
      
      if (record.is_empty || !record.check_in || !record.check_out) {
        emptyDays++;
        if (record.leave_type) {
          leaveBreakdown[record.leave_type]++;
        }
      } else {
        workingDays++;
        const hours = parseFloat(record.total_hours) || 0;
        totalMinutes += hours * 60;
        
        if (hours > 0) {
          dailyHours.push({ date: record.date, hours, employee: record.employees });
        }
        
        if (record.is_late) lateDays++;
        if (record.is_early_out) earlyOutDays++;
        if (!record.is_late && !record.is_early_out) {
          regularDays++;
          onTimeDays++;
        }
      }
    });
    
    const totalHours = totalMinutes / 60;
    const avgHoursPerDay = workingDays > 0 ? totalHours / workingDays : 0;
    const attendanceRate = data.length > 0 ? (workingDays / data.length) * 100 : 0;
    const onTimeRate = workingDays > 0 ? (onTimeDays / workingDays) * 100 : 0;
    
    // Find best and worst days
    dailyHours.sort((a, b) => b.hours - a.hours);
    const bestDay = dailyHours[0];
    const worstDay = dailyHours[dailyHours.length - 1];
    
    // Calculate overtime days (>8 hours)
    const overtimeDays = dailyHours.filter(d => d.hours > 8).length;
    
    return {
      totalHours,
      totalMinutes,
      workingDays,
      emptyDays,
      totalDays: data.length,
      lateDays,
      earlyOutDays,
      regularDays,
      onTimeDays,
      avgHoursPerDay,
      attendanceRate,
      onTimeRate,
      leaveBreakdown,
      bestDay,
      worstDay,
      overtimeDays,
      uniqueEmployees: uniqueEmployees.size
    };
  }, [filteredAttendance, attendance]);

  const fetchCompanyId = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: companyData, error } = await supabase
        .from('companies')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setCompanyId(companyData.id);
    } catch (error) {
      message.error('Error fetching company data');
    }
  };

  const fetchAttendance = async (company_id) => {
  if (!dateRange || dateRange.length !== 2) return;
  
  try {
    setLoading(true);
    
    const startDate = dateRange[0].format('YYYY-MM-DD');
    const endDate = dateRange[1].format('YYYY-MM-DD');
    
    // This now returns ALL dates (including Sundays) with Sunday automatically marked as DAY OFF
    const completeMatrix = await generateCompleteAttendanceMatrix(startDate, endDate, company_id);
    
    // Simply format the records without additional Sunday logic
    const formattedRecords = completeMatrix.map(record => ({
      ...record,
      key: record.id || `${record.employee_id}_${record.date}`,
      check_in: formatTime(record.check_in),
      check_out: formatTime(record.check_out),
      total_hours: Number(record.total_hours || 0).toFixed(2),
      total_hours_formatted: formatHoursMinutes(Number(record.total_hours || 0))
    }));

    setAttendance(formattedRecords);

  } catch (error) {
    console.error('Error fetching attendance:', error);
    message.error('Error fetching attendance data');
  } finally {
    setLoading(false);
  }
};
  const handleDateRangeChange = (dates) => {
    if (dates) {
      const [start, end] = dates;
      setDateRange([
        start.startOf('day'),
        end.endOf('day')
      ]);
    }
  };

  const handleUpload = async (file) => {
  try {
    setLoading(true);
    
    const isExcelFile = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                       file.type === 'application/vnd.ms-excel' ||
                       file.name.toLowerCase().endsWith('.xlsx') ||
                       file.name.toLowerCase().endsWith('.xls');
    
    if (isExcelFile) {
      const arrayBuffer = await file.arrayBuffer();
      const preview = await timesheetService.previewImport(arrayBuffer, file.name);
      
      if (preview.success) {
        if (preview.preview?.employeeMapping?.newEmployees && 
            preview.preview.employeeMapping.newEmployees.length > 0) {
          setNewEmployeesList(preview.preview.employeeMapping.newEmployees);
          setNewEmployeesWarning(true);
        } else {
          setPreviewData(preview);
          setUploadedFile({ file, content: arrayBuffer, isXlsx: true });
          setImportPreviewVisible(true);
        }
      } else {
        message.error('Error parsing Excel file: ' + preview.error);
      }
    } else {
      const text = await file.text();
      const preview = await timesheetService.previewImport(text, file.name);
      
      if (preview.success) {
        if (preview.preview?.employeeMapping?.newEmployees && 
            preview.preview.employeeMapping.newEmployees.length > 0) {
          setNewEmployeesList(preview.preview.employeeMapping.newEmployees);
          setNewEmployeesWarning(true);
        } else {
          setPreviewData(preview);
          setUploadedFile({ file, content: text, isXlsx: false });
          setImportPreviewVisible(true);
        }
      } else {
        message.error('Error parsing CSV: ' + preview.error);
      }
    }
  } catch (error) {
    console.error('Upload error:', error);
    message.error('Error reading file: ' + error.message);
  } finally {
    setLoading(false);
  }
  return false;
};



const handleGoToEmployees = () => {
  setNewEmployeesWarning(false);
  navigate('/dashboard/employees');
};

  const handleConfirmImport = async () => {
    if (!uploadedFile || !companyId) return;

    try {
      setImporting(true);
      const entries = timesheetService.parseCSV(uploadedFile.content);
      await timesheetService.saveRawEntries(entries, companyId);
      await timesheetService.processTimesheet(entries, companyId);
      
      message.success('Timesheet processed successfully');
      setActiveTab('records');
      setImportPreviewVisible(false);
      setPreviewData(null);
      setUploadedFile(null);
      fetchAttendance(companyId);
    } catch (error) {
      message.error('Error processing timesheet: ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  const handleCancelImport = () => {
    setImportPreviewVisible(false);
    setPreviewData(null);
    setUploadedFile(null);
  };

  const handleDirectUpload = async (file) => {
  try {
    setLoading(true);
    
    const isExcelFile = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                       file.type === 'application/vnd.ms-excel' ||
                       file.name.toLowerCase().endsWith('.xlsx') ||
                       file.name.toLowerCase().endsWith('.xls');
    
    if (isExcelFile) {
      const arrayBuffer = await file.arrayBuffer();
      const entries = timesheetService.parseCSV(arrayBuffer, file.name);
      await timesheetService.saveRawEntries(entries, companyId);
      await timesheetService.processTimesheet(entries, companyId);
      message.success('Excel timesheet processed successfully');
    } else {
      const text = await file.text();
      const entries = timesheetService.parseCSV(text, file.name);
      await timesheetService.saveRawEntries(entries, companyId);
      await timesheetService.processTimesheet(entries, companyId);
      message.success('CSV timesheet processed successfully');
    }

    setActiveTab('records');
    fetchAttendance(companyId);
  } catch (error) {
    console.error('Direct upload error:', error);
    if (error.type === 'NEW_EMPLOYEES_BLOCKED') {
      setNewEmployeesList(error.newEmployees);
      setNewEmployeesWarning(true);
    } else {
      message.error('Error processing timesheet: ' + error.message);
    }
  } finally {
    setLoading(false);
  }
  return false;
};

  const applyFilters = () => {
    let filteredData = [...attendance];
    
    if (nameFilter) {
      const searchTerm = nameFilter.toLowerCase();
      filteredData = filteredData.filter(record => {
        const firstName = (record.employees?.first_name || '').toLowerCase();
        const lastName = (record.employees?.last_name || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName.includes(searchTerm);
      });
    }
    
    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'regular':
          filteredData = filteredData.filter(record => !record.is_late && !record.is_early_out && !record.is_empty);
          break;
        case 'late':
          filteredData = filteredData.filter(record => record.is_late);
          break;
        case 'early':
          filteredData = filteredData.filter(record => record.is_early_out);
          break;
        case 'absent':
          filteredData = filteredData.filter(record => record.is_empty || record.total_hours === '0.00');
          break;
        default:
          break;
      }
    }
    
    setFilteredAttendance(filteredData);
  };

  const handleNameFilterChange = (e) => {
    setNameFilter(e.target.value);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };
  
  const showEditModal = (record) => {
    setCurrentRecord(record);
    setIsEmptyRecord(record.is_empty || record.total_hours === '0.00');
    
    const checkInTime = record.check_in !== '--:--' ? 
      dayjs(record.check_in, 'HH:mm') : null;
    
    const checkOutTime = record.check_out !== '--:--' ? 
      dayjs(record.check_out, 'HH:mm') : null;
    
    editForm.setFieldsValue({
      check_in: checkInTime,
      check_out: checkOutTime,
      leave_type: record.leave_type || undefined
    });
    
    setIsEditModalVisible(true);
  };
  
  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setCurrentRecord(null);
    setIsEmptyRecord(false);
    editForm.resetFields();
  };
  
  const handleLeaveTypeChange = (value) => {
    const parsed = parseLeaveType(value);
    
    if (parsed.hours > 0) {
      const startTime = dayjs().hour(9).minute(0);
      const endTime = startTime.add(parsed.hours, 'hour');
      
      editForm.setFieldsValue({
        check_in: startTime,
        check_out: endTime
      });
    } else {
      editForm.setFieldsValue({
        check_in: null,
        check_out: null
      });
    }
  };
  
  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      setUpdatingRecord(true);
      const hasSyntheticId = !currentRecord?.id || String(currentRecord.id).startsWith('empty_');
      
      const leaveTypeData = values.leave_type ? parseLeaveType(values.leave_type) : null;
      
      const checkIn = values.check_in ? values.check_in.format('HH:mm:ss') : null;
      const checkOut = values.check_out ? values.check_out.format('HH:mm:ss') : null;
      
      let totalHours = 0;
      if (values.check_in && values.check_out) {
        totalHours = values.check_out.diff(values.check_in, 'hour', true);
        totalHours = Math.max(0, totalHours);
      }
      
      const date = currentRecord.date;
      const standardStart = dayjs(`${date} 09:00:00`);
      const standardEnd = dayjs(`${date} 17:00:00`);
      
      const isLate = values.check_in ? 
        dayjs(`${date} ${checkIn}`).isAfter(standardStart) : 
        currentRecord.is_late;
      
      const isEarlyOut = values.check_out ? 
        dayjs(`${date} ${checkOut}`).isBefore(standardEnd) : 
        currentRecord.is_early_out;
      
      const recordData = {
        check_in: checkIn,
        check_out: checkOut,
        total_hours: totalHours.toFixed(2),
        is_late: isLate,
        is_early_out: isEarlyOut,
        leave_type: leaveTypeData?.type || null,
        status: totalHours > 0 || leaveTypeData?.type ? 'present' : 'absent',
        updated_at: dayjs().toISOString()
      };
      
      if (currentRecord.is_empty || hasSyntheticId) {
        const { error } = await supabase
          .from('timesheet_daily_records')
          .insert([{
            company_id: companyId,
            employee_id: currentRecord.employee_id,
            date: currentRecord.date,
            ...recordData,
            created_at: dayjs().toISOString()
          }]);
        
        if (error) throw error;
        message.success('Record created successfully');
      } else {
        const { error } = await supabase
          .from('timesheet_daily_records')
          .update(recordData)
          .eq('id', currentRecord.id);
        
        if (error) throw error;
        message.success('Record updated successfully');
      }
      
      fetchAttendance(companyId);
      setIsEditModalVisible(false);
      
    } catch (error) {
      console.error('Error updating record:', error);
      message.error('Failed to update record: ' + error.message);
    } finally {
      setUpdatingRecord(false);
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 130,
      render: (text) => {
        const dateObj = dayjs(text);
        const isSunday = dateObj.day() === 0;
        return (
          <span className="stack-cell">
            <span className="num" style={{ fontWeight: 600 }}>{dateObj.format('DD MMM YYYY')}</span>
            <span className="stack-cell__sub" style={isSunday ? { color: 'var(--theme-danger-ink)', fontWeight: 600 } : undefined}>
              {dateObj.format('dddd')}
            </span>
          </span>
        );
      },
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: 'Employee',
      key: 'employee',
      width: 200,
      render: (_, record) => {
        const fullName = `${record.employees?.first_name || ''} ${record.employees?.last_name || ''}`.trim() || 'Unknown';
        const department = record.employees?.department || 'Unassigned';

        return (
          <span className="stack-cell">
            <span style={{ fontWeight: 600 }}>{fullName}</span>
            <span className="stack-cell__sub">{department}</span>
          </span>
        );
      }
    },
    {
      title: 'Check in',
      dataIndex: 'check_in',
      key: 'check_in',
      width: 120,
      align: 'center',
      render: (time, record) => (
        <span className="stack-cell" style={{ alignItems: 'center', gap: 2 }}>
          <span className={`num ${!time || time === '--:--' ? 'num--muted' : ''}`} style={{ fontWeight: 600 }}>
            {time || '--:--'}
          </span>
          {record.is_late && <StatusPill tone="warning">Late</StatusPill>}
        </span>
      ),
    },
    {
      title: 'Check out',
      dataIndex: 'check_out',
      key: 'check_out',
      width: 120,
      align: 'center',
      render: (time, record) => (
        <span className="stack-cell" style={{ alignItems: 'center', gap: 2 }}>
          <span className={`num ${!time || time === '--:--' ? 'num--muted' : ''}`} style={{ fontWeight: 600 }}>
            {time || '--:--'}
          </span>
          {record.is_early_out && <StatusPill tone="warning">Early</StatusPill>}
        </span>
      ),
    },
    {
      title: 'Hours',
      key: 'total_hours_display',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const numHours = Number(record.total_hours || 0);
        return (
          <span className="stack-cell" style={{ alignItems: 'center', gap: 2 }}>
            <span className={`num ${numHours > 0 ? 'num--strong' : 'num--muted'}`}>
              {record.total_hours_formatted}
            </span>
            {record.is_empty && <StatusPill tone="warning">No data</StatusPill>}
          </span>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      render: (_, record) => {
        const status = getAttendanceStatus(record);
        return <StatusPill tone={STATUS_COLOR_TONES[status.color] || 'neutral'}>{status.text}</StatusPill>;
      },
    },
    {
      title: '',
      key: 'action',
      width: 64,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={record.is_empty ? 'Add leave or absence details' : 'Edit time record'}>
          <Button
            type="text"
            className="icon-action"
            data-tone={record.is_empty ? 'warning' : 'primary'}
            aria-label={record.is_empty ? 'Add leave or absence details' : 'Edit time record'}
            icon={record.is_empty ? <PlusOutlined /> : <EditOutlined />}
            onClick={() => showEditModal(record)}
          />
        </Tooltip>
      ),
    },
  ];

  const previewColumns = [
    { title: 'Employee ID', dataIndex: 'fingerprint_id', key: 'fingerprint_id' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Entries', dataIndex: 'entryCount', key: 'entryCount' },
    { title: 'First Entry', dataIndex: 'firstEntry', key: 'firstEntry', render: (text) => text ? dayjs(text).format('DD/MM/YYYY HH:mm') : '-' },
    { title: 'Last Entry', dataIndex: 'lastEntry', key: 'lastEntry', render: (text) => text ? dayjs(text).format('DD/MM/YYYY HH:mm') : '-' },
  ];

  const leaveChips = smartStats
    ? [
        ['SICK LEAVE', 'Sick', 'warning'],
        ['LOCAL LEAVE', 'Local', 'primary'],
        ['PH', 'Public holiday', 'primary'],
        ['ABSENT', 'Absent', 'danger'],
        ['OFF', 'Day off', 'neutral'],
      ].filter(([key]) => smartStats.leaveBreakdown[key] > 0)
    : [];

  const recordsTab = (
    <div className="page" style={{ gap: 16 }}>
      {smartStats && (
        <>
          <StatGrid columns={6}>
            <StatCard
              label="Total hours"
              tone="primary"
              icon={<ClockCircleOutlined />}
              value={formatHoursMinutes(smartStats.totalHours)}
              compact
            />
            <StatCard
              label="Working days"
              tone="positive"
              icon={<CheckCircleOutlined />}
              value={smartStats.workingDays}
              unit={`/ ${smartStats.totalDays}`}
              compact
            />
            <StatCard
              label="Empty records"
              tone="warning"
              icon={<WarningOutlined />}
              value={smartStats.emptyDays}
              compact
            />
            <StatCard
              label="Avg hours / day"
              tone="neutral"
              icon={<RiseOutlined />}
              value={formatHoursMinutes(smartStats.avgHoursPerDay)}
              compact
            />
            <StatCard
              label="Attendance rate"
              tone={smartStats.attendanceRate >= 90 ? 'positive' : smartStats.attendanceRate >= 75 ? 'warning' : 'danger'}
              icon={<TeamOutlined />}
              value={`${Math.round(smartStats.attendanceRate)}%`}
              meter={smartStats.attendanceRate / 100}
              compact
            />
            <StatCard
              label="On-time rate"
              tone={smartStats.onTimeRate >= 80 ? 'positive' : smartStats.onTimeRate >= 60 ? 'warning' : 'danger'}
              icon={<ThunderboltOutlined />}
              value={`${Math.round(smartStats.onTimeRate)}%`}
              meter={smartStats.onTimeRate / 100}
              compact
            />
          </StatGrid>

          <Card size="small">
            <Row gutter={[24, 16]}>
              <Col xs={24} md={8}>
                <span className="eyebrow">
                  {nameFilter ? `Performance · ${smartStats.uniqueEmployees} employee${smartStats.uniqueEmployees > 1 ? 's' : ''}` : 'Performance'}
                </span>
                <Space size={6} wrap>
                  <StatusPill tone="positive">{smartStats.regularDays} regular</StatusPill>
                  <StatusPill tone="warning">{smartStats.lateDays} late</StatusPill>
                  <StatusPill tone="warning">{smartStats.earlyOutDays} early out</StatusPill>
                </Space>
              </Col>
              <Col xs={24} md={8}>
                <span className="eyebrow">Leave and absence</span>
                <Space size={6} wrap>
                  {leaveChips.length === 0 && <span className="text-muted">None in this range</span>}
                  {leaveChips.map(([key, label, tone]) => (
                    <StatusPill key={key} tone={tone}>
                      {smartStats.leaveBreakdown[key]} {label}
                    </StatusPill>
                  ))}
                </Space>
              </Col>
              <Col xs={24} md={8}>
                <span className="eyebrow">Highlights</span>
                <Space direction="vertical" size={2}>
                  {smartStats.bestDay && (
                    <span>
                      <TrophyOutlined style={{ color: 'var(--theme-warning-ink)', marginRight: 6 }} />
                      Longest day <strong>{formatHoursMinutes(smartStats.bestDay.hours)}</strong> on {dayjs(smartStats.bestDay.date).format('D MMM')}
                    </span>
                  )}
                  {smartStats.overtimeDays > 0 && (
                    <span>
                      <ThunderboltOutlined style={{ color: 'var(--theme-primary-ink)', marginRight: 6 }} />
                      <strong>{smartStats.overtimeDays}</strong> days over 8 hours
                    </span>
                  )}
                  {!smartStats.bestDay && smartStats.overtimeDays === 0 && <span className="text-muted">Nothing to highlight yet</span>}
                </Space>
              </Col>
            </Row>
          </Card>
        </>
      )}

      <Card className="table-card">
        <div className="toolbar" style={{ padding: '12px 8px 8px' }}>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="DD/MM/YYYY"
            allowClear={false}
            presets={[
              { label: 'Today', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
              { label: 'This Week', value: [dayjs().startOf('week'), dayjs().endOf('week')] },
              { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
              { label: 'Last Month', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
            ]}
          />
          <Input
            placeholder="Search employee"
            prefix={<SearchOutlined />}
            value={nameFilter}
            onChange={handleNameFilterChange}
            style={{ width: 220 }}
            allowClear
          />
          <Select
            placeholder="Filter status"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={(value) => handleStatusFilterChange(value || 'all')}
            allowClear
          >
            <Option value="all">All statuses</Option>
            <Option value="regular">Regular</Option>
            <Option value="late">Late</Option>
            <Option value="early">Early out</Option>
            <Option value="absent">Absent / empty</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={filteredAttendance.length > 0 ? filteredAttendance : attendance}
          rowKey={(record) => record.id || `${record.employee_id}_${record.date}`}
          loading={loading}
          size="middle"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`
          }}
          scroll={{ x: 960 }}
          rowClassName={(record) => record.is_empty ? 'attendance-empty-row' : ''}
        />
      </Card>
    </div>
  );

  const importTab = (
    <div className="page" style={{ gap: 16 }}>
      <Alert
        message="Import timesheet data"
        description="Upload the CSV or Excel export from your fingerprint machine. After import, the system builds a complete matrix with every employee for every day in the range, so missing days show up as empty records you can fill in."
        type="info"
        showIcon
      />
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card style={{ height: '100%' }}>
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <span className="stat-card__icon" data-tone="primary" style={{ width: 40, height: 40 }}><EyeOutlined /></span>
              <h3 className="section-title">Preview, then import</h3>
              <p className="section-subtitle" style={{ marginTop: 0 }}>
                Recommended. Check the employees, entry counts and dates found in the file before anything is saved.
              </p>
              <Upload beforeUpload={handleUpload} showUploadList={false} accept=".csv,.xlsx,.xls">
                <Button icon={<EyeOutlined />} loading={loading} type="primary">
                  Choose file to preview
                </Button>
              </Upload>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card style={{ height: '100%' }}>
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <span className="stat-card__icon" data-tone="neutral" style={{ width: 40, height: 40 }}><UploadOutlined /></span>
              <h3 className="section-title">Direct import</h3>
              <p className="section-subtitle" style={{ marginTop: 0 }}>
                Saves and processes the file straight away. Use it for files you have already checked.
              </p>
              <Upload beforeUpload={handleDirectUpload} showUploadList={false} accept=".csv,.xlsx,.xls">
                <Button icon={<UploadOutlined />} loading={loading}>
                  Choose file to import
                </Button>
              </Upload>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );

  return (
    <Page>
      <PageHeader
        title="Attendance"
        subtitle="Daily clock-ins from the fingerprint machine, with leave and absences."
        actions={
          activeTab !== 'import' && (
            <Button icon={<ImportOutlined />} onClick={() => setActiveTab('import')}>
              Import attendance
            </Button>
          )
        }
      />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'records',
            label: <span><CalendarOutlined /> Attendance records</span>,
            children: recordsTab,
          },
          {
            key: 'import',
            label: <span><ImportOutlined /> Import data</span>,
            children: importTab,
          },
        ]}
      />

      {/* Edit Modal */}
      <Modal
        title={isEmptyRecord ? "Add Leave/Absence Details" : "Edit Time Record"}
        open={isEditModalVisible}
        onCancel={handleEditCancel}
        footer={[
          <Button key="cancel" onClick={handleEditCancel}>Cancel</Button>,
          <Button key="submit" type="primary" loading={updatingRecord} onClick={handleEditSubmit}>
            {isEmptyRecord ? 'Create Record' : 'Save Changes'}
          </Button>,
        ]}
        width={600}
      >
        <Form form={editForm} layout="vertical">
          {currentRecord && (
            <div className="soft-panel kv-list" style={{ marginBottom: 16 }}>
              <div>
                <span className="kv-list__label">Employee</span>
                <span className="kv-list__value">{`${currentRecord.employees?.first_name || ''} ${currentRecord.employees?.last_name || ''}`.trim() || 'Unknown'}</span>
              </div>
              <div>
                <span className="kv-list__label">Date</span>
                <span className="kv-list__value">{dayjs(currentRecord.date).format('ddd D MMM YYYY')}</span>
              </div>
              <div>
                <span className="kv-list__label">Department</span>
                <span className="kv-list__value">{currentRecord.employees?.department || 'Unassigned'}</span>
              </div>
            </div>
          )}

          {isEmptyRecord && (
            <Alert message="Empty Record" description="This employee has no attendance record for this day. Use the dropdown below to specify the leave type or absence reason." type="warning" showIcon style={{ marginBottom: 16 }} />
          )}

          {isEmptyRecord && (
            <Form.Item name="leave_type" label="Leave Type / Absence Reason">
              <Select placeholder="Select leave type or absence reason" onChange={handleLeaveTypeChange} allowClear>
                {getLeaveTypeOptions().map(option => (
                  <Option key={option.value} value={option.value}>{option.label}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="check_in" label="Check In Time">
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="check_out" label="Check Out Time">
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Import Preview Modal */}
      <Modal
        title="Import Preview"
        open={importPreviewVisible}
        onCancel={handleCancelImport}
        width={800}
        footer={[
          <Button key="cancel" onClick={handleCancelImport}>Cancel</Button>,
          <Button key="import" type="primary" loading={importing} onClick={handleConfirmImport} icon={<ImportOutlined />}>
            Confirm Import
          </Button>,
        ]}
      >
        {previewData && (
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Alert message="Preview Import Data" description="Review the data below before confirming the import." type="warning" showIcon />
            <div className="soft-panel kv-list">
              <div>
                <span className="kv-list__label">Total entries</span>
                <span className="kv-list__value">{previewData.preview.totalEntries}</span>
              </div>
              <div>
                <span className="kv-list__label">Employees</span>
                <span className="kv-list__value">{previewData.preview.employeeCount}</span>
              </div>
              <div>
                <span className="kv-list__label">Date range</span>
                <span className="kv-list__value">
                  {previewData.preview.dateRange ? `${previewData.preview.dateRange.start} to ${previewData.preview.dateRange.end}` : 'N/A'}
                </span>
              </div>
            </div>
            <span className="eyebrow" style={{ marginBottom: -8 }}>Employee summary</span>
            <Table columns={previewColumns} dataSource={previewData.preview.employees} rowKey="fingerprint_id" size="small" pagination={{ pageSize: 10 }} scroll={{ y: 300 }} />
          </Space>
        )}
      </Modal>

      {/* New Employees Warning Modal */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: 'var(--theme-warning-ink)' }} />
            <span>Import Blocked - New Employees Detected</span>
          </Space>
        }
        open={newEmployeesWarning}
        onCancel={() => setNewEmployeesWarning(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setNewEmployeesWarning(false)}>
            Close
          </Button>,
          <Button
            key="goto"
            type="primary"
            icon={<UserOutlined />}
            onClick={handleGoToEmployees}
          >
            Go to Employees Page
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="Import Cannot Proceed"
            description={`The CSV file contains ${newEmployeesList.length} employee(s) that do not exist in the system. Please add these employees first.`}
            type="error"
            showIcon
          />

          <div>
            <span className="eyebrow">New employees found</span>
            <List
              size="small"
              bordered
              dataSource={newEmployeesList}
              renderItem={(employee) => (
                <List.Item>
                  <span className="stack-cell">
                    <span style={{ fontWeight: 600 }}>{employee.name || 'Unknown'}</span>
                    <span className="stack-cell__sub">Employee ID {employee.fingerprint_id}</span>
                  </span>
                </List.Item>
              )}
            />
          </div>

          <Alert
            message="Next Steps"
            description={
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                <li>Click "Go to Employees Page" below</li>
                <li>Add each employee using the same Employee ID shown above</li>
                <li>Return here and try importing again</li>
              </ol>
            }
            type="info"
            showIcon
          />
        </Space>
      </Modal>
    </Page>
  );
};

export default Attendance;
